import React, { useMemo } from 'react'

import { Icon, PopOver } from 'components'
import MenuList, { OptionType } from 'components/MenuList'
import { faEnvelopeSquare } from '@fortawesome/pro-light-svg-icons/faEnvelopeSquare'

import { EmailComposer } from 'store/types/lead'
import { PopOverItem } from 'components/PopOver/types'
import { Lead, useAppDispatch } from 'store'
import { LogEmailInfo } from '../Windows/EmailLog/LogEmailComposser'
import { LeadDetails } from 'types/global'
import { SendEmailInfo } from '../Windows/EmailLog/SendEmailComposser'

import useUser from 'hooks/use-user'

import { LOG_EMAIL, SEND_EMAIL } from 'utils/constants'
import { emptyRecipient } from '../Windows/EmailLog/utils'

type Props = {
  children?: React.ReactNode
  id: string
  onSelect?: (recipient: string) => void
  recipient?: LeadDetails.Email | null
  isMenuList?: boolean
  isPortaled?: boolean
  isDisabled?: boolean
}

const EmailLogOptions = ({
  children,
  id,
  onSelect,
  recipient,
  isMenuList,
  isPortaled = true,
  isDisabled = false,
}: Props) => {
  const dispatch = useAppDispatch()
  const { currentUser } = useUser()

  const handleOpenEmailCompser = (recipient?: Props['recipient']) => {
    const readOnlyRecipient = recipient ? [recipient] : []

    const sendEmailInfo: SendEmailInfo = {
      readOnlyAddresses: {
        recipients: { leadContact: [...readOnlyRecipient], usersList: [], newEmails: [] },
        cc: emptyRecipient,
        bcc: emptyRecipient,
      },
      addresses: {
        recipients: emptyRecipient,
        cc: emptyRecipient,
        bcc: emptyRecipient,
      },
      from: {
        value: currentUser?.email,
        fullName: currentUser?.fullName,
      },
      subject: '',
      body: '',
      defaultAttachments: [],
      attachments: [],
    }

    const initComposer: EmailComposer = {
      emailId: id,
      emailInfo: sendEmailInfo,
      emailType: SEND_EMAIL,
    }

    dispatch(
      Lead.addLeadDetailWindow({
        data: {
          initialValues: initComposer,
          leadId: '',
          referenceId: '',
        },
        type: 'email',
        windowState: 'active',
      }),
    )
    if (onSelect) onSelect(recipient?.value ?? '')
  }

  const handleOpenLogEmailCompser = (recipient?: Props['recipient']) => {
    const readOnlyRecipient = recipient ? [recipient] : []

    const logEmailInfo: LogEmailInfo = {
      readOnlyAddresses: {
        recipients: { leadContact: [...readOnlyRecipient], usersList: [], newEmails: [] },
        cc: [],
      },
      addresses: {
        recipients: emptyRecipient,
        cc: [],
      },
      from: {
        leadContact: [],
        usersList: [],
        newEmails: [],
      },
      readOnlyFromEmail: {
        leadContact: [
          {
            value: currentUser?.email,
            fullName: currentUser?.fullName,
          },
        ],
        usersList: [],
        newEmails: [],
      },
      subject: '',
      body: '',
      defaultAttachments: [],
      attachments: [],
    }

    const initComposer: EmailComposer = {
      emailId: id,
      emailInfo: logEmailInfo,
      emailType: LOG_EMAIL,
    }

    dispatch(
      Lead.addLeadDetailWindow({
        data: {
          initialValues: initComposer,
          leadId: '',
          referenceId: '',
        },
        type: 'email',
        windowState: 'active',
      }),
    )
    if (onSelect) onSelect(recipient?.value ?? '')
  }

  const handleSelectItem = (item?: PopOverItem) => {
    if (!item) {
      return
    }

    if (item?.value == SEND_EMAIL) {
      handleOpenEmailCompser(recipient)
    }

    if (item?.value == LOG_EMAIL) {
      handleOpenLogEmailCompser(recipient)
    }
  }

  const options: OptionType[] = useMemo(
    () => [
      {
        icon: ['far', 'envelope'],
        label: 'send_email_lbl',
        value: SEND_EMAIL,
      },
      {
        icon: <Icon fontSize={21} icon={faEnvelopeSquare} />,
        label: 'log_email_lbl',
        value: LOG_EMAIL,
      },
    ],
    [],
  )

  if (isMenuList) {
    const menuOptions: OptionType[] = options.map((i) => ({
      label: i.label as string,
      value: i.value as string,
      icon: i.icon as React.ReactNode | string[],
    }))
    return <MenuList options={menuOptions} onSelect={(v) => handleSelectItem({ label: v, value: v })} />
  }

  return (
    <PopOver
      icon={['far', 'list']}
      title="disposition_lbl"
      options={options}
      onChange={handleSelectItem}
      textColor="defaultText"
      isPortaled={isPortaled}
      isDisabled={isDisabled}
    >
      {children}
    </PopOver>
  )
}

export default EmailLogOptions
